import React, { useCallback, useEffect, useState } from "react";
import { ICartItemHeadless, TAgeFilter, TCartInfo, TGameOptions, TRaceTypeFilter, TSelectedVariants, TTrackTypes } from "../../types";
import { AiOutlineClose } from "react-icons/ai";
import TrackTile from "../TrackSelection/TrackSelectionTile";
import { formatDateWithSuffix } from "../../helpers/utils";
import { SampleVariantsBlock } from "../common/ProductSectionAndDate/SampleVariantBlock";
import { ImSpinner6 } from "react-icons/im";
import { ShoppingCartIcon } from "@heroicons/react/solid";
import { ADD_TO_CART_LINES } from "../../graphqlQueries";
import { getAdminIdFromStoreFrontId } from "../../helpers/shopify";
import { useTrackContext } from "../../hooks/useTrackContext";
import { query } from "../../lib";

interface IUpsellModalProps {
  modalTitle: string;
  upsellVariants: TSelectedVariants[];
  toggleModalDisplay: () => void;
  isModalOpen: boolean;
  trackOptions: {
    title: TTrackTypes;
    trackBanner: string;
  };
  ageGroup?: TAgeFilter;
  raceType?: TRaceTypeFilter;
  numberOfSeats: number;
  numberOfRaces?: number;
  productRenderedLocation: string;
  headlessCartInfo: TCartInfo;
  isDuplicateModal?: boolean;
}

const UpsellModal: React.FC<IUpsellModalProps> = ({
  upsellVariants,
  toggleModalDisplay,
  isModalOpen,
  trackOptions,
  ageGroup,
  raceType,
  numberOfSeats,
  numberOfRaces,
  productRenderedLocation,
  headlessCartInfo,
  modalTitle,
  isDuplicateModal,
}) => {
  const { dispatch, trackSelectionOption, headlessCartTotalAmount, headlessCart } = useTrackContext();
  const [isProductAdding, setIsProductAdding] = useState<boolean>(false);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState<boolean>(false);

  // Check if this variant is already in the cart
  const isVariantInCart = useCallback(async () => {
    if (!upsellVariants || upsellVariants.length === 0) return false;
    const currentVariantId = upsellVariants[0]?.variantsSelectedObject?.id;

    if (productRenderedLocation === "external") {
      return headlessCart.some((item) => item.merchandise.id === currentVariantId);
    } else {
      try {
        const response = await fetch("/cart.js");
        const cart = await response.json();
        const adminId = getAdminIdFromStoreFrontId(currentVariantId);
        return cart.items.some((item: any) => item.variant_id === adminId);
      } catch (error) {
        console.error("Error checking cart:", error);
        return false;
      }
    }
  }, [upsellVariants, headlessCart, productRenderedLocation]);

  useEffect(() => {
    const checkCart = async () => {
      const isInCart = await isVariantInCart();
      setShowDuplicateWarning(isInCart);
    };
    checkCart();
  }, [isVariantInCart]);

  useEffect(() => {
    if (upsellVariants?.length > 0) {
      const getFirstUpsellVariant = upsellVariants[0];
      if (getFirstUpsellVariant) {
        const getPriceAmount = parseFloat(getFirstUpsellVariant.variantsSelectedObject.priceV2.amount);
        setTotalPrice(getPriceAmount);
      }
    }
  }, [upsellVariants]);

  const addToCart = useCallback(async () => {
    if (productRenderedLocation === "external") {
      try {
        setIsProductAdding(true);

        const lines = {
          items: isDuplicateModal
            ? upsellVariants.map((variant) => ({
                merchandiseId: variant.variantsSelectedObject.id,
                quantity: numberOfSeats,
              }))
            : upsellVariants
                .map((variant, index) => {
                  if (index === 0) {
                    return {
                      merchandiseId: variant.variantsSelectedObject.id,
                      quantity: numberOfSeats,
                    };
                  }
                  return null;
                })
                .filter((item): item is NonNullable<typeof item> => item !== null),
        };

        const addToCart = await query(ADD_TO_CART_LINES, {
          cartId: headlessCartInfo.id,
          lines: lines.items,
        });

        const isAddedToCart = addToCart?.data?.cartLinesAdd;

        if (isAddedToCart) {
          const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
          dispatch({
            type: "SET_CARTITEMS_HEADLESS",
            payload: {
              headlessCart: [...cartItems] as ICartItemHeadless[],
            },
          });

          const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: cartTotalAmount,
            },
          });
          dispatch({
            type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
            payload: {
              isHeadlessMiniCartIsShowing: true,
            },
          });
        } else {
          console.error("isAddedToCart Cart data is missing in the GraphQL response.", isAddedToCart);
        }
      } catch (error) {
        console.error("GraphQL request failed:", error);
      } finally {
        setIsProductAdding(false);
        toggleModalDisplay();
      }
    } else {
      setIsProductAdding(true);

      let formData = !isDuplicateModal
        ? {
            items: upsellVariants
              .filter((_, index) => index === 0)
              .map((variant) => ({
                id: getAdminIdFromStoreFrontId(variant.variantsSelectedObject.id),
                quantity: numberOfSeats,
                properties: {
                  upsellModalItem: "true",
                },
              })),
          }
        : {
            items: upsellVariants.map((variant) => ({
              id: getAdminIdFromStoreFrontId(variant.variantsSelectedObject.id),
              quantity: numberOfSeats,
            })),
          };

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });

        const cart = await response.json();

        if (cart.status === 200) {
          miniCartCallback(true);
        } else {
          miniCartCallback(false);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsProductAdding(false);
        toggleModalDisplay();
      }
    }
  }, [dispatch, headlessCartInfo, isDuplicateModal, numberOfSeats, productRenderedLocation, toggleModalDisplay, upsellVariants]);

  const handleAddToCartClick = useCallback(() => {
    if (!upsellVariants || upsellVariants.length === 0) {
      alert("Need to select a time");
      return;
    }
    addToCart();
  }, [upsellVariants, addToCart]);

  return (
    <>
      {isModalOpen && (
        <div className="tw-fixed tw-z-[100000] tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-900 tw-bg-opacity-50 tw-p-[10px] lg:tw-p-0">
          <div
            className={`tw-max-w-[998px] tw-bg-[#393B41] tw-max-h-[80vh] tw-overflow-y-auto tw-h-fit tw-w-full tw-mx-auto tw-rounded-[25px] tw-shadow-lg tw-transform tw-transition-all tw-duration-300 tw-scale-100`}
          >
            <div className="tw-px-[10px] tw-pb-[20px] lg:tw-px-[40px]">
              <div className="tw-flex lg:tw-justify-center tw-items-center tw-relative tw-py-[20px]">
                <div className="tw-text-white tw-text-[20px] tw-font-[700] tw-text-center">{modalTitle}</div>
                <div className="tw-hidden md:tw-block tw-absolute tw-right-0">
                  <button className="tw-text-gray-600 tw-text-lg tw-hover:text-gray-800 tw-focus:tw-outline-none" onClick={toggleModalDisplay}>
                    <AiOutlineClose className="tw-w-[30px] tw-h-[30px] tw-text-[#C2C0C3] tw-font-[400]" />
                  </button>
                </div>
              </div>
              <div className="tw-space-y-4">
                <div className="tw-block lg:tw-flex tw-justify-center lg:tw-items-center lg:tw-gap-x-16">
                  <TrackTile
                    onDoubleClick={() => console.log("Double Click has fired")}
                    isSelected={false}
                    displayBorders={true}
                    title={trackOptions.title}
                    image={trackOptions.trackBanner}
                  />
                  <div className="tw-text-white">
                    {ageGroup && raceType && (
                      <span className="tw-text-[20px] tw-font-[700] tw-block tw-mt-[10px] tw-capitalize tw-text-center lg:tw-text-left">
                        Ages {ageGroup} | {raceType.toLowerCase()}
                      </span>
                    )}

                    <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-center tw-flex-col lg:tw-justify-start tw-mt-[10px]">
                      <span className="tw-flex tw-gap-3.5">
                        <span>{trackSelectionOption.title === "VIRTUAL_REALITY" ? "Player(s):" : "Seat(s):"}</span> <span>{numberOfSeats}</span>
                      </span>
                      {numberOfRaces && (
                        <span className="tw-flex tw-gap-3.5">
                          <span>{trackSelectionOption.title === "VIRTUAL_REALITY" ? "Session(s):" : "Race(s):"}</span> <span>{numberOfRaces}</span>
                        </span>
                      )}
                    </span>

                    <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-center tw-flex-col lg:tw-justify-start tw-mt-[10px]">
                      {upsellVariants.length > 0 && (
                        <>
                          {upsellVariants.every(
                            (variant) => formatDateWithSuffix(new Date(variant.variantSelectedTime)) === formatDateWithSuffix(new Date(upsellVariants[0].variantSelectedTime))
                          ) ? (
                            <span className="tw-text-[18px] tw-font-[700] tw-block tw-my-[10px] tw-text-center lg:tw-text-left">
                              {formatDateWithSuffix(new Date(upsellVariants[0].variantSelectedTime))}
                            </span>
                          ) : (
                            <>
                              {upsellVariants.map((variant, index) => (
                                <span key={index} className="tw-text-[18px] tw-font-[700] tw-block tw-my-[10px] tw-text-center lg:tw-text-left">
                                  {formatDateWithSuffix(new Date(variant.variantSelectedTime))}
                                </span>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </span>

                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-[10px]">
                      {upsellVariants.length &&
                        upsellVariants.map((variant, index) => {
                          if (isDuplicateModal) {
                            const gameTitle = variant.variantsSelectedObject.selectedOptions.find((option) => option.name.includes("Time"));
                            const gameName = variant.variantsSelectedObject.selectedOptions.find((option) => option.name.includes("Game"));
                            const variantTitle = trackSelectionOption.title === "VIRTUAL_REALITY" ? gameTitle?.value : variant.variantsSelectedObject.title;

                            return (
                              <div key={variant.variantsSelectedObject.id} className="tw-flex tw-flex-col tw-justify-center">
                                <div className="tw-flex tw-flex-col tw-justify-center tw-w-fit tw-mx-auto lg:tw-mx-0">
                                  {variant.variantsSelectedObject && (
                                    <SampleVariantsBlock
                                      isGameVR={trackSelectionOption.title === "VIRTUAL_REALITY"}
                                      variantId={variant.variantsSelectedObject.id}
                                      key={variant.variantsSelectedObject.id}
                                      title={variantTitle as string}
                                      price={variant.variantsSelectedObject.priceV2.amount}
                                      variant={variant.variantsSelectedObject}
                                      vrGameName={(gameName?.value as TGameOptions) || undefined}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          } else if (index === 0) {
                            const gameTitle = variant.variantsSelectedObject.selectedOptions.find((option) => option.name.includes("Time"));
                            const gameName = variant.variantsSelectedObject.selectedOptions.find((option) => option.name.includes("Game"));
                            const variantTitle = trackSelectionOption.title === "VIRTUAL_REALITY" ? gameTitle?.value : variant.variantsSelectedObject.title;

                            return (
                              <div key={variant.variantsSelectedObject.id} className="tw-flex tw-flex-col tw-justify-center">
                                <div className="tw-flex tw-flex-col tw-justify-center tw-w-fit tw-mx-auto lg:tw-mx-0">
                                  {variant.variantsSelectedObject && (
                                    <SampleVariantsBlock
                                      isGameVR={trackSelectionOption.title === "VIRTUAL_REALITY"}
                                      variantId={variant.variantsSelectedObject.id}
                                      key={variant.variantsSelectedObject.id}
                                      title={variantTitle as string}
                                      price={variant.variantsSelectedObject.priceV2.amount}
                                      variant={variant.variantsSelectedObject}
                                      vrGameName={(gameName?.value as TGameOptions) || undefined}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return [];
                        })}
                    </div>

                    <span className="tw-text-[18px] tw-font-[600] tw-block tw-mt-[10px] tw-text-center lg:tw-text-left">Total</span>
                    <span className="tw-text-[30px] tw-font-[700] tw-leading-[1] tw-mt-[10px] tw-block tw-text-center lg:tw-text-left">
                      $<span>{(totalPrice * numberOfSeats + Number(headlessCartTotalAmount)).toFixed(2)}</span>
                    </span>
                    <span className="tw-text-[12px] md:tw-text-[14px] tw-font-[500] tw-block tw-my-[20px] tw-text-center lg:tw-text-left tw-flex tw-items-center tw-justify-center">
                      Get <span className="tw-font-[700] tw-px-1"> $19 off </span> with Hyper Karting{" "}
                      <a href="https://hyperkarting.com.au/products/membership">
                        <img
                          src="https://cdn.shopify.com/s/files/1/0496/8733/0976/files/HK-Membership_Icon_a819308e-9913-473d-b1ac-b5aa3d061947.png?v=1695350485"
                          alt="membership logo"
                          style={{
                            maxWidth: "150px",
                            width: "100%",
                          }}
                        />
                      </a>
                    </span>
                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-[20px]">
                      <button
                        onClick={() => {
                          if (showDuplicateWarning) {
                            addToCart();
                          } else {
                            handleAddToCartClick();
                          }
                        }}
                        className={`tw-flex tw-items-center tw-justify-center tw-text-center tw-text-white lg:tw-max-w-[230px] lg:tw-w-full tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] bg-gradient-2 tw-rounded-full tw-cursor-pointer`}
                        id="add-to-cart-button"
                      >
                        <>
                          {isProductAdding ? (
                            <>
                              <ImSpinner6 className="tw-w-[26px] tw-h-[26px] tw-animate-spin tw-transition tw-duration-300 tw-ease-in-out" />
                              <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Adding...</span>
                            </>
                          ) : (
                            <>
                              <ShoppingCartIcon className="tw-w-[26px] tw-h-[26px]" />
                              <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">{showDuplicateWarning ? "Continue" : "Add to Cart"}</span>
                            </>
                          )}
                        </>
                      </button>
                      <button
                        onClick={toggleModalDisplay}
                        className="tw-flex tw-items-center tw-justify-center tw-text-center tw-font-extrabold tw-text-[medium] tw-text-[#9D62FE] tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] lg:tw-max-w-[230px] lg:tw-w-full tw-border-[#9D62FE] tw-border tw-rounded-full tw-cursor-pointer"
                      >
                        {showDuplicateWarning ? "Cancel" : "Dismiss"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpsellModal;
